/**
 * Development-only 3D collision diagnostics:
 *  - collider wireframes straight from Rapier (what the physics actually sees)
 *  - player capsule outline at the simulated (not interpolated) position
 *  - velocity (yellow), desired displacement (magenta) vs resolved (green),
 *    both scaled to 0.25 s so they are visible
 *  - contact points and normals of this tick's sweep hits (white/red if lethal)
 *  - ground normal (cyan) and nearby contact normals (blue)
 * Comparing desired vs resolved separates movement-rule problems (wrong
 * desired motion) from collision problems (wrong resolution).
 */
import * as THREE from 'three';
import type { Simulation } from '../simulation/Simulation';
import type { PhysicsWorld } from '../simulation/PhysicsWorld';

const MAX_SEGMENTS = 512;
const DISPLAY_SECONDS = 0.25;

export class PhysicsDebugView {
  readonly group = new THREE.Group();
  private readonly colliderLines: THREE.LineSegments;
  private readonly dynamic: THREE.LineSegments;
  private readonly positions = new Float32Array(MAX_SEGMENTS * 6);
  private readonly colors = new Float32Array(MAX_SEGMENTS * 6);
  private count = 0;

  constructor(physics: PhysicsWorld) {
    this.group.name = 'physics-debug';
    this.group.visible = false;
    const { vertices, colors } = physics.debugLines();
    const cg = new THREE.BufferGeometry();
    cg.setAttribute('position', new THREE.BufferAttribute(vertices.slice(), 3));
    cg.setAttribute('color', new THREE.BufferAttribute(colors.slice(), 4));
    this.colliderLines = new THREE.LineSegments(cg, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.55, depthTest: false }));
    this.colliderLines.renderOrder = 10;
    this.group.add(this.colliderLines);

    const dg = new THREE.BufferGeometry();
    dg.setAttribute('position', new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
    dg.setAttribute('color', new THREE.BufferAttribute(this.colors, 3).setUsage(THREE.DynamicDrawUsage));
    this.dynamic = new THREE.LineSegments(dg, new THREE.LineBasicMaterial({ vertexColors: true, depthTest: false }));
    this.dynamic.frustumCulled = false;
    this.dynamic.renderOrder = 11;
    this.group.add(this.dynamic);
  }

  get visible(): boolean {
    return this.group.visible;
  }

  set visible(v: boolean) {
    this.group.visible = v;
  }

  private seg(ax: number, ay: number, az: number, bx: number, by: number, bz: number, color: number): void {
    if (this.count >= MAX_SEGMENTS) return;
    const i = this.count * 6;
    this.positions.set([ax, ay, az, bx, by, bz], i);
    const r = ((color >> 16) & 255) / 255;
    const g = ((color >> 8) & 255) / 255;
    const b = (color & 255) / 255;
    this.colors.set([r, g, b, r, g, b], i);
    this.count++;
  }

  update(sim: Simulation): void {
    if (!this.group.visible) return;
    this.count = 0;
    const st = sim.state;
    const shape = sim.motor.shape;
    const p = st.position;
    const r = shape.radius;
    const top = shape.halfSegment * 2 + r * 2;
    const cy = p.y + top / 2;
    // Capsule outline: 4 vertical lines + rings at the segment ends.
    for (let k = 0; k < 16; k++) {
      const a0 = (k / 16) * Math.PI * 2;
      const a1 = ((k + 1) / 16) * Math.PI * 2;
      for (const y of [p.y + r, p.y + top - r]) {
        this.seg(p.x + Math.cos(a0) * r, y, p.z + Math.sin(a0) * r, p.x + Math.cos(a1) * r, y, p.z + Math.sin(a1) * r, 0x9cff9c);
      }
      if (k % 4 === 0) this.seg(p.x + Math.cos(a0) * r, p.y + r, p.z + Math.sin(a0) * r, p.x + Math.cos(a0) * r, p.y + top - r, p.z + Math.sin(a0) * r, 0x9cff9c);
    }
    const v = st.velocity;
    const s = DISPLAY_SECONDS;
    this.seg(p.x, cy, p.z, p.x + v.x * s, cy + v.y * s, p.z + v.z * s, 0xffe14d);
    const m = sim.motor.diagnostics.motion;
    const k = s * sim.config.simulation.tickRate;
    const d = m.desired;
    this.seg(p.x, cy + 0.05, p.z, p.x + d.x * k, cy + 0.05 + d.y * k, p.z + d.z * k, 0xff4dff);
    const rx = (p.x - st.previousPosition.x) * k;
    const ry = (p.y - st.previousPosition.y) * k;
    const rz = (p.z - st.previousPosition.z) * k;
    this.seg(p.x, cy - 0.05, p.z, p.x + rx, cy - 0.05 + ry, p.z + rz, 0x4dff88);
    for (let i = 0; i < m.hitCount; i++) {
      const h = m.hits[i]!;
      const c = h.material?.kind === 'lethal' ? 0xff3030 : 0xffffff;
      const q = h.point;
      const e = 0.08;
      this.seg(q.x - e, q.y, q.z, q.x + e, q.y, q.z, c);
      this.seg(q.x, q.y - e, q.z, q.x, q.y + e, q.z, c);
      this.seg(q.x, q.y, q.z, q.x + h.normal.x * 0.6, q.y + h.normal.y * 0.6, q.z + h.normal.z * 0.6, c);
    }
    if (st.grounded) {
      const n = st.groundNormal;
      this.seg(p.x, p.y, p.z, p.x + n.x, p.y + n.y, p.z + n.z, 0x40e0ff);
    }
    for (let i = 0; i < st.contactCount; i++) {
      const c = st.contacts[i]!;
      this.seg(p.x, cy, p.z, p.x - c.normal.x * 0.8, cy - c.normal.y * 0.8, p.z - c.normal.z * 0.8, c.kind === 'lethal' ? 0xff3030 : 0x4d7dff);
    }
    const g = this.dynamic.geometry;
    g.setDrawRange(0, this.count * 2);
    (g.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (g.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
  }

  dispose(): void {
    this.colliderLines.geometry.dispose();
    (this.colliderLines.material as THREE.Material).dispose();
    this.dynamic.geometry.dispose();
    (this.dynamic.material as THREE.Material).dispose();
  }
}
